import { useEffect } from "react";
import { Admin, CustomRoutes, Resource } from "react-admin";
import { Route, useLocation } from "react-router-dom";

import { authProvider, dataProvider, queryClient } from "./api";
import { AccountHistory } from "./components/account-history";
import { Dashboard } from "./components/analytics";
import {
  AuditLogsConfigVariables,
  AuditLogsProjects,
} from "./components/audit-logs";
import {
  AdminRoleMembershipCreate,
  AdminUserCreate,
  CompanyCreate,
  ConfigVariableCreate,
  DemoProjectTemplateCreate,
  DemoProjectUserCreate,
  EnterprisePlanCreate,
  EnterprisePlanInvoiceCreate,
  InAppPurchaseCreate,
  JobInfoCreate,
  ManualWorkerCreate,
  OtcMaintenanceWindowCreate,
  SsoInfoCreate,
  StressTestingProjectCreate,
} from "./components/create";
import { CustomLayout, CustomLogin, NotFound } from "./components/custom";
import {
  AccountEdit,
  AdminUserEdit,
  ApiKeyEdit,
  CompanyEdit,
  ConfigVariableEdit,
  DemoProjectTemplateEdit,
  DemoProjectUserEdit,
  EnterprisePlanEdit,
  EnterprisePlanInvoiceEdit,
  InAppPurchaseEdit,
  JobInfoEdit,
  OtcMaintenanceWindowEdit,
  ProjectEdit,
  SsoInfoEdit,
  UserEdit,
} from "./components/edit";
import { IosReleaseStatus } from "./components/ios-release-status";
import {
  AccountList,
  AdminRoleMembershipList,
  AdminUserList,
  ApiKeyList,
  CanceledEnterpriseOwnerList,
  CanceledInAppPurchaseUserList,
  CanceledStripeOwnerList,
  ChargeList,
  CompanyList,
  ConfigVariableList,
  DemoProjectTemplateList,
  DemoProjectUserList,
  EnterprisePlanInvoiceList,
  EnterprisePlanList,
  InAppPurchaseList,
  JobInfoList,
  ManualWorkerList,
  OtcMaintenanceWindowList,
  PayingEnterpriseOwnerList,
  PayingInAppPurchaseUserList,
  PayingStripeOwnerList,
  ProjectList,
  SsoInfoList,
  SyncErrorList,
  SystemConfigurationList,
  UserList,
} from "./components/list";
import {
  AccountShow,
  AdminRoleMembershipShow,
  AdminUserShow,
  ApiKeyShow,
  BoxTokenShow,
  ChargeShow,
  CompanyShow,
  ConfigVariableShow,
  DemoProjectTemplateShow,
  DemoProjectUserShow,
  DropboxTokenShow,
  EnterprisePlanInvoiceShow,
  EnterprisePlanShow,
  InAppPurchaseShow,
  JobInfoShow,
  ManualWorkerShow,
  MicrosoftTokenShow,
  OtcMaintenanceWindowShow,
  ProjectShow,
  SsoInfoShow,
  SyncErrorShow,
  UserShow,
} from "./components/show";
import { Sidekiq } from "./components/sidekiq";
import { SystemStatus } from "./components/system-status";
import { theme } from "./config";
import { storage } from "./storage";
import { store } from "./store";
import {
  getRegionalResourceName,
  Resource as FwResource,
  ResourceRegional,
} from "./types";
import {
  PATH_DASHBOARD,
  PATH_IOS_RELEASE_STATUS,
  PATH_SIDEKIQ,
  PATH_SYSTEM_STATUS,
  PATH_TEMPLATE_US_HISTORY_FOR_ACCOUNT,
  routes,
} from "./utils/Routes";

function App() {
  // We are allowed to utilize the `useLocation` hook here
  // because `App` is wrapped in a `HashRouter` in index.tsx.
  const { pathname } = useLocation();
  const regions = storage.enumerateRegions();

  // Scroll to the top of the page when the path changes.
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
    });
  }, [pathname]);

  return (
    <Admin
      authProvider={authProvider}
      dataProvider={dataProvider}
      queryClient={queryClient}
      catchAll={NotFound}
      layout={CustomLayout}
      loginPage={CustomLogin}
      store={store}
      theme={theme}
      disableTelemetry
      requireAuth
    >
      {/*
        NOTE: React Admin uses the first <Resource /> as the default list page
        when someone first visits the application. Therefore, we put the Users
        resource first so it is the default. This also allows us to be in line
        with Admin V1.
      */}
      <Resource
        name={FwResource.User}
        options={{ label: "Users" }}
        edit={UserEdit}
        list={UserList}
        show={UserShow}
      />
      <Resource
        name={FwResource.Account}
        options={{ label: "Accounts" }}
        edit={AccountEdit}
        list={AccountList}
        show={AccountShow}
      />
      <Resource
        name={FwResource.AdminRoleMembership}
        options={{ label: "Admin Role Memberships" }}
        create={AdminRoleMembershipCreate}
        list={AdminRoleMembershipList}
        show={AdminRoleMembershipShow}
      />
      <Resource
        name={FwResource.AdminUser}
        options={{ label: "Admin Users" }}
        create={AdminUserCreate}
        edit={AdminUserEdit}
        list={AdminUserList}
        show={AdminUserShow}
      />
      <Resource
        name={FwResource.ApiKey}
        options={{ label: "Api Keys" }}
        edit={ApiKeyEdit}
        list={ApiKeyList}
        show={ApiKeyShow}
      />
      <Resource
        name={FwResource.Charge}
        options={{ label: "Charges" }}
        list={ChargeList}
        show={ChargeShow}
      />
      <Resource
        name={FwResource.Company}
        options={{ label: "Companies" }}
        create={CompanyCreate}
        edit={CompanyEdit}
        list={CompanyList}
        show={CompanyShow}
      />
      {regions.flatMap((region) => {
        const regionLabel = region.toUpperCase();
        const name = (r: ResourceRegional) =>
          getRegionalResourceName(region, r);
        return [
          <Resource
            key={name(ResourceRegional.ConfigVariable)}
            name={name(ResourceRegional.ConfigVariable)}
            options={{ label: `Config Variables - ${regionLabel}` }}
            create={ConfigVariableCreate}
            edit={ConfigVariableEdit}
            list={<ConfigVariableList region={region} />}
            show={ConfigVariableShow}
          />,
          <Resource
            key={name(ResourceRegional.DemoProjectTemplate)}
            name={name(ResourceRegional.DemoProjectTemplate)}
            options={{ label: `Demo Project Templates - ${regionLabel}` }}
            create={DemoProjectTemplateCreate}
            edit={DemoProjectTemplateEdit}
            list={DemoProjectTemplateList}
            show={DemoProjectTemplateShow}
          />,
          <Resource
            key={name(ResourceRegional.DemoProjectUser)}
            name={name(ResourceRegional.DemoProjectUser)}
            options={{ label: `Demo Project Users - ${regionLabel}` }}
            create={DemoProjectUserCreate}
            edit={DemoProjectUserEdit}
            list={DemoProjectUserList}
            show={DemoProjectUserShow}
          />,
          <Resource
            key={name(ResourceRegional.FeatureFlag)}
            name={name(ResourceRegional.FeatureFlag)}
            options={{ label: `System Configuration - ${regionLabel}` }}
            list={SystemConfigurationList}
          />,
          <Resource
            key={name(ResourceRegional.ManualWorker)}
            name={name(ResourceRegional.ManualWorker)}
            options={{ label: `Manual Workers - ${regionLabel}` }}
            create={<ManualWorkerCreate region={region} />}
            list={ManualWorkerList}
            show={ManualWorkerShow}
          />,
          <Resource
            key={name(ResourceRegional.Project)}
            name={name(ResourceRegional.Project)}
            options={{ label: `Projects - ${regionLabel}` }}
            edit={ProjectEdit}
            list={ProjectList}
            show={<ProjectShow region={region} />}
          />,
          <Resource
            key={name(ResourceRegional.StressTestingProject)}
            name={name(ResourceRegional.StressTestingProject)}
            options={{ label: `Project for Stress Testing - ${regionLabel}` }}
            create={<StressTestingProjectCreate region={region} />}
          />,
          <Resource
            key={name(ResourceRegional.SyncError)}
            name={name(ResourceRegional.SyncError)}
            options={{ label: `Sync Errors - ${regionLabel}` }}
            list={SyncErrorList}
            show={<SyncErrorShow region={region} />}
          />,
          // NOTE: Will not show in side menu as resource doesn't have a list page.
          <Resource
            key={name(ResourceRegional.BoxToken)}
            name={name(ResourceRegional.BoxToken)}
            options={{ label: `Box Tokens - ${regionLabel}` }}
            show={<BoxTokenShow region={region} />}
          />,
          <Resource
            key={name(ResourceRegional.DropboxToken)}
            name={name(ResourceRegional.DropboxToken)}
            options={{ label: `Dropbox Tokens - ${regionLabel}` }}
            show={<DropboxTokenShow region={region} />}
          />,
          <Resource
            key={name(ResourceRegional.MicrosoftToken)}
            name={name(ResourceRegional.MicrosoftToken)}
            options={{ label: `Microsoft Tokens - ${regionLabel}` }}
            show={<MicrosoftTokenShow region={region} />}
          />,
        ];
      })}

      <Resource
        name={FwResource.EnterprisePlanInvoice}
        options={{ label: "Enterprise Plan Invoices" }}
        create={EnterprisePlanInvoiceCreate}
        edit={EnterprisePlanInvoiceEdit}
        list={EnterprisePlanInvoiceList}
        show={EnterprisePlanInvoiceShow}
      />
      <Resource
        name={FwResource.EnterprisePlan}
        options={{ label: "Enterprise Plans" }}
        create={EnterprisePlanCreate}
        edit={EnterprisePlanEdit}
        list={EnterprisePlanList}
        show={EnterprisePlanShow}
      />
      <Resource
        name={FwResource.InAppPurchase}
        options={{ label: "In App Purchases" }}
        create={InAppPurchaseCreate}
        edit={InAppPurchaseEdit}
        list={InAppPurchaseList}
        show={InAppPurchaseShow}
      />
      <Resource
        name={FwResource.JobInfo}
        options={{ label: "Job Infos" }}
        create={JobInfoCreate}
        edit={JobInfoEdit}
        list={JobInfoList}
        show={JobInfoShow}
      />
      <Resource
        name={FwResource.OtcMaintenanceWindow}
        options={{ label: "Maintenance Window" }}
        create={OtcMaintenanceWindowCreate}
        edit={OtcMaintenanceWindowEdit}
        list={OtcMaintenanceWindowList}
        show={OtcMaintenanceWindowShow}
      />
      <Resource
        name={FwResource.SsoInfo}
        options={{ label: "Sso Infos" }}
        create={SsoInfoCreate}
        edit={SsoInfoEdit}
        list={SsoInfoList}
        show={SsoInfoShow}
      />
      <Resource
        name={FwResource.PayingStripeOwner}
        options={{ label: "Paying Users (Stripe)" }}
        list={PayingStripeOwnerList}
      />
      <Resource
        name={FwResource.CanceledStripeOwner}
        options={{ label: "Paying Users (Stripe Canceled)" }}
        list={CanceledStripeOwnerList}
      />
      <Resource
        name={FwResource.PayingEnterpriseOwner}
        options={{ label: "Enterprise Accounts" }}
        list={PayingEnterpriseOwnerList}
      />
      <Resource
        name={FwResource.CanceledEnterpriseOwner}
        options={{ label: "Enterprise Accounts (Canceled)" }}
        list={CanceledEnterpriseOwnerList}
      />
      <Resource
        name={FwResource.PayingInAppPurchaseUser}
        options={{ label: "Paying Users (In App)" }}
        list={PayingInAppPurchaseUserList}
      />
      <Resource
        name={FwResource.CanceledInAppPurchaseUser}
        options={{ label: "Paying Users (In App Canceled)" }}
        list={CanceledInAppPurchaseUserList}
      />
      <CustomRoutes>
        <Route
          path={PATH_TEMPLATE_US_HISTORY_FOR_ACCOUNT}
          element={<AccountHistory />}
        />
        {regions.flatMap((region) => [
          <Route
            key={getRegionalResourceName(
              region,
              ResourceRegional.AuditLogConfigVariables,
            )}
            path={getRegionalResourceName(
              region,
              ResourceRegional.AuditLogConfigVariables,
            )}
            element={<AuditLogsConfigVariables region={region} />}
          />,
          <Route
            key={getRegionalResourceName(
              region,
              ResourceRegional.AuditLogProject,
            )}
            path={routes.computeAuditLogPathTemplateForProject(region)}
            element={<AuditLogsProjects region={region} />}
          />,
        ])}
        <Route path={PATH_DASHBOARD} element={<Dashboard />} />
        <Route path={PATH_IOS_RELEASE_STATUS} element={<IosReleaseStatus />} />
        <Route path={PATH_SIDEKIQ} element={<Sidekiq />} />
        <Route path={PATH_SYSTEM_STATUS} element={<SystemStatus />} />
      </CustomRoutes>
    </Admin>
  );
}

export default App;
